import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Play, Square, Clock, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTimeTracking } from '@/contexts/TimeTrackingContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format minutes to HH:MM
const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const TimeTrackingWidget = ({ taskId, taskTitle, projectId, onTimeUpdated }) => {
  const [entries, setEntries] = useState([]);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualHours, setManualHours] = useState('');
  const [manualMinutes, setManualMinutes] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const timerIntervalRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch time entries
  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tasks/${taskId}/time-entries`, { headers });
      setEntries(res.data);
    } catch (err) {
      console.error('Error fetching time entries', err);
    }
  };

  // Check for active timer
  const checkActiveTimer = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/time-entries/active`, { headers });
      if (res.data && res.data.task_id === taskId) {
        setActiveTimer(res.data);
        setIsTimerRunning(true);
        // Calculate elapsed seconds from start_time, not from elapsed_minutes
        const startTime = new Date(res.data.start_time);
        const now = new Date();
        const elapsedSec = Math.floor((now - startTime) / 1000);
        setElapsedSeconds(elapsedSec);
      } else {
        setIsTimerRunning(false);
        setActiveTimer(null);
        setElapsedSeconds(0);
      }
    } catch (err) {
      console.error('Error checking active timer', err);
    }
  };

  useEffect(() => {
    fetchEntries();
    checkActiveTimer();
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [taskId]);

  // Timer tick
  useEffect(() => {
    if (isTimerRunning) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isTimerRunning]);

  // Start timer
  const handleStartTimer = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/time-entries/start?task_id=${taskId}`, {}, { headers });
      setActiveTimer(res.data);
      setIsTimerRunning(true);
      setElapsedSeconds(0);
      toast.success('Timer gestartet');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Starten des Timers');
    }
  };

  // Stop timer
  const handleStopTimer = async () => {
    try {
      await axios.post(`${API_URL}/api/time-entries/stop`, {}, { headers });
      setIsTimerRunning(false);
      setActiveTimer(null);
      setElapsedSeconds(0);
      fetchEntries();
      if (onTimeUpdated) onTimeUpdated();
      toast.success('Timer gestoppt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Stoppen des Timers');
    }
  };

  // Add manual entry
  const handleAddManualEntry = async () => {
    const hours = parseInt(manualHours) || 0;
    const mins = parseInt(manualMinutes) || 0;
    const totalMinutes = hours * 60 + mins;
    
    if (totalMinutes <= 0) {
      toast.error('Bitte geben Sie eine gültige Zeit ein');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/time-entries`, {
        task_id: taskId,
        duration_minutes: totalMinutes,
        description: manualDescription || null
      }, { headers });
      
      setShowManualDialog(false);
      setManualHours('');
      setManualMinutes('');
      setManualDescription('');
      fetchEntries();
      if (onTimeUpdated) onTimeUpdated();
      toast.success('Zeiteintrag hinzugefügt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  // Delete entry
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Zeiteintrag wirklich löschen?')) return;
    try {
      await axios.delete(`${API_URL}/api/time-entries/${entryId}`, { headers });
      fetchEntries();
      if (onTimeUpdated) onTimeUpdated();
      toast.success('Zeiteintrag gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Calculate total time
  const totalMinutes = entries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);

  // Format elapsed time
  const formatElapsed = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Timer Section */}
      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
        <Clock className="w-5 h-5 text-muted-foreground" />
        
        {isTimerRunning ? (
          <>
            <div className="flex-1">
              <div className="text-2xl font-mono font-bold text-primary">
                {formatElapsed(elapsedSeconds)}
              </div>
              <div className="text-xs text-muted-foreground">Timer läuft...</div>
            </div>
            <Button variant="destructive" size="sm" onClick={handleStopTimer}>
              <Square className="w-4 h-4 mr-1" />
              Stop
            </Button>
          </>
        ) : (
          <>
            <div className="flex-1">
              <div className="text-sm font-medium">Zeiterfassung</div>
              <div className="text-xs text-muted-foreground">
                Gesamt: {formatDuration(totalMinutes)}
              </div>
            </div>
            <Button variant="default" size="sm" onClick={handleStartTimer}>
              <Play className="w-4 h-4 mr-1" />
              Start
            </Button>
            <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Manuell
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Zeit manuell erfassen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label>Stunden</Label>
                      <Input
                        type="number"
                        min="0"
                        value={manualHours}
                        onChange={(e) => setManualHours(e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="flex-1">
                      <Label>Minuten</Label>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={manualMinutes}
                        onChange={(e) => setManualMinutes(e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Beschreibung (optional)</Label>
                    <Input
                      value={manualDescription}
                      onChange={(e) => setManualDescription(e.target.value)}
                      placeholder="Was wurde gemacht?"
                    />
                  </div>
                  <Button onClick={handleAddManualEntry} className="w-full">
                    Hinzufügen
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      {/* Time Entries List */}
      {entries.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">Zeiteinträge</div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between p-2 bg-background border rounded text-sm"
              >
                <div className="flex-1">
                  <div className="font-medium">{formatDuration(entry.duration_minutes)}</div>
                  <div className="text-xs text-muted-foreground">
                    {entry.user_name} • {formatDate(entry.created_at)}
                    {entry.description && ` • ${entry.description}`}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteEntry(entry.id)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Global Timer Widget for Header - Uses shared TimeTrackingContext
export const GlobalTimerWidget = ({ onNavigateToTimer }) => {
  const { activeTimer, elapsedSeconds, stopTimer, formatElapsed } = useTimeTracking();

  const handleStopTimer = async (e) => {
    e.stopPropagation();
    await stopTimer();
  };

  const handleNavigate = () => {
    if (activeTimer && onNavigateToTimer) {
      onNavigateToTimer(activeTimer.project_id, activeTimer.task_id);
    }
  };

  if (!activeTimer) return null;

  return (
    <div 
      className="flex items-center gap-2 px-3 py-1.5 bg-red-500/15 border border-red-500/30 rounded-full cursor-pointer hover:bg-red-500/25 transition-colors"
      onClick={handleNavigate}
      data-testid="active-timer-widget"
      title={`Timer läuft für: ${activeTimer.task_title} - Klicken zum Anzeigen`}
    >
      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      <div className="text-sm">
        <span className="font-mono font-bold text-red-600">{formatElapsed(elapsedSeconds)}</span>
        <span className="text-muted-foreground ml-2 hidden sm:inline max-w-[150px] truncate">{activeTimer.task_title}</span>
      </div>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleStopTimer} 
        className="h-6 px-2 hover:bg-red-500/20 hover:text-red-600"
        data-testid="stop-timer-btn"
      >
        <Square className="w-3 h-3" />
      </Button>
    </div>
  );
};

// Hook to check for active timer - wrapper around TimeTrackingContext for backward compatibility
export const useActiveTimer = () => {
  const { activeTimer, checkActiveTimer } = useTimeTracking();
  return { activeTimer, checkActiveTimer };
};

export default TimeTrackingWidget;
